"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import Pagination from "./Pagination";
import SearchBar from "./SearchBar";
import FilterPanel from "./FilterPanel";
import { ImCross } from "react-icons/im";
import { MdDelete } from "react-icons/md";
import { FaEdit, FaCheck } from "react-icons/fa";
import { DEFAULT_COLUMN_WIDTH, DEFAULT_PAGE_SIZE } from "./constants";
import "./styles/dynamicTable.css";

const TableUI = ({
  config,
  data,
  filteredData,
  setFilteredData,
  handleDeleteRow,
  currentPage,
  totalItems,
  handlePageChange,
  handleRowSelection,
  selectedRows,
  handleUpdateRow,
  isLoading,
  setTotalItems,
}) => {
  const [editingRow, setEditingRow] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterColumn, setFilterColumn] = useState(null);
  const tableRef = useRef(null);

  // Initialize column widths from config
  useEffect(() => {
    const initialWidths = {};
    config.columns.forEach((column) => {
      initialWidths[column.key] = column.width || DEFAULT_COLUMN_WIDTH;
    });
    setColumnWidths(initialWidths);
  }, [config.columns]);

  // Check if mobile view
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // Handle search and filtering
  useEffect(() => {
    if (!data || data.length === 0) return;
    let results = [...data];
    // Apply search
    if (searchTerm) {
      results = results.filter((row) => {
        return config.columns.some((column) => {
          const cellValue = row[column.key];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue)
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        });
      });
    }

    // Apply filters
    if (Object.keys(activeFilters).length > 0) {
      results = results.filter((row) => {
        return Object.entries(activeFilters).every(([key, values]) => {
          if (!values || values.length === 0) return true;
          const cellValue = String(row[key]).toLowerCase();
          return values.some((value) =>
            cellValue.includes(String(value).toLowerCase())
          );
        });
      });
    }
    setFilteredData(results);
    setTotalItems(results?.length);
    handlePageChange(1);
  }, [data, searchTerm, activeFilters, config.columns]);

  // Get paginated data
  const paginatedData = useMemo(() => {
    if (!filteredData) return [];

    const pageSize = config.pagination?.pageSize || DEFAULT_PAGE_SIZE;
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage, config.pagination]);

  // Handle row selection
  const handleRowSelect = (row) => {
    const newSelectedRows = [...selectedRows];
    const rowExists = newSelectedRows.find((r) => r.id === row.id);
    if (rowExists) {
      const updatedRows = newSelectedRows.filter((r) => r.id !== row.id);
      handleRowSelection(updatedRows);
    } else {
      newSelectedRows.push(row);
      handleRowSelection(newSelectedRows);
    }
  };

  // Handle select all rows
  const handleSelectAll = () => {
    if (selectedRows?.length) {
      handleRowSelection([]);
    } else {
      handleRowSelection(data);
    }
  };

  // Handle edit row
  const handleEditRow = (id) => {
    setEditingRow(id);
    setEditedValues(paginatedData?.find((item) => item?.id === id));
  };

  // Handle save row
  const handleSaveRow = (id) => {
    handleUpdateRow(editedValues, id);
    setEditingRow(null);
    setEditedValues({});
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditedValues({});
  };

  // Handle cell value change
  const handleCellChange = (key, value) => {
    setEditedValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Handle column resize start
  const handleResizeStart = (e, key) => {
    e.preventDefault();
    setResizingColumn(key);
    setStartX(e.clientX);
  };

  // Handle column resize move
  const handleResizeMove = useCallback(
    (e) => {
      if (!resizingColumn) return;

      const diff = e.clientX - startX;
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: `${Math.max(
          50,
          Number.parseInt(prev[resizingColumn]) + diff
        )}px`,
      }));
      setStartX(e.clientX);
    },
    [resizingColumn, startX]
  );

  // Handle column resize end
  const handleResizeEnd = useCallback(() => {
    setResizingColumn(null);
  }, []);

  // Add resize event listeners
  useEffect(() => {
    if (resizingColumn) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    };
  }, [resizingColumn, handleResizeMove, handleResizeEnd]);

  // Handle filter click
  const handleFilterClick = (column) => {
    setFilterColumn(column);
    setShowFilterPanel(true);
  };

  // Handle filter apply
  const handleFilterApply = (columnKey, values) => {
    setActiveFilters((prev) => ({
      ...prev,
      [columnKey]: values,
    }));
    setShowFilterPanel(false);
  };

  // Handle filter clear
  const handleFilterClear = (columnKey) => {
    setActiveFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnKey];
      return newFilters;
    });
    setShowFilterPanel(false);
  };

  const loaderUI = () => {
    return (
      <div className="flex items-center w-full justify-center h-32">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  };
  // Render mobile view
  const renderMobileView = () => {
    return (
      <div className="space-y-4">
        {isLoading ? (
          loaderUI()
        ) : (
          <>
            {paginatedData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No data available
              </div>
            ) : (
              paginatedData.map((row, rowIndex) => {
                const isSelected = selectedRows.some((r) => r.id === row.id);

                return (
                  <div
                    key={row.id || rowIndex}
                    className={`p-4 border rounded-lg shadow-sm ${
                      isSelected ? "bg-primary/10" : "bg-white"
                    } ${
                      editingRow === row?.id
                        ? "border-primary"
                        : "border-gray-200"
                    }`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        {config.showRowSelection !== false && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleRowSelect(row)}
                            className="mr-2 h-4 w-4 text-primary"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </div>
                      <div className="flex space-x-2">
                        {editingRow === row?.id ? (
                          <>
                            <button
                              onClick={() => handleSaveRow(row?.id)}
                              className="p-1 text-green-600 hover:text-green-800"
                              title="Save"
                            >
                              <FaCheck />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="p-1 text-gray-600 hover:text-gray-800"
                              title="Cancel"
                            >
                              <ImCross className="text-sm" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleEditRow(row?.id)}
                            className="p-1 text-blue-600 hover:text-blue-800"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteRow(row?.id)}
                          className="p-1 text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <MdDelete className="text-lg" />
                        </button>
                      </div>
                    </div>

                    {config.columns.map((column) => (
                      <div key={column.key} className="py-1">
                        <div className="text-sm font-medium text-gray-500">
                          {column.label}
                        </div>
                        <div className="mt-1">
                          {editingRow === row?.id && column.isEditable ? (
                            <input
                              type="text"
                              value={editedValues[column.key] || ""}
                              onChange={(e) =>
                                handleCellChange(column.key, e.target.value)
                              }
                              className="w-full p-1 border border-gray-300 rounded"
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <div className="truncate">
                              {column.customCellRenderer
                                ? column.customCellRenderer(row)
                                : row[column.key]}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="dynamic-table-container">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-2">
        {config.searchable && (
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        )}
      </div>

      {showFilterPanel && filterColumn && (
        <FilterPanel
          column={filterColumn}
          data={data}
          activeFilters={activeFilters[filterColumn.key] || []}
          onApply={(values) => handleFilterApply(filterColumn.key, values)}
          onClear={() => handleFilterClear(filterColumn.key)}
          onClose={() => setShowFilterPanel(false)}
        />
      )}

      {isMobile ? (
        renderMobileView()
      ) : (
        <div className="overflow-x-auto" ref={tableRef}>
          <table className="min-w-full divide-y divide-gray-200 border-collapse">
            <TableHeader
              columns={config.columns}
              showActions={config?.showActions}
              columnWidths={columnWidths}
              handleResizeStart={handleResizeStart}
              selectedRows={selectedRows}
              paginatedData={paginatedData}
              handleSelectAll={handleSelectAll}
              activeFilters={activeFilters}
              onFilterClick={handleFilterClick}
              showRowSelection={config.showRowSelection !== false}
            />

            <TableBody
              loaderUI={loaderUI}
              isLoading={isLoading}
              columns={config.columns}
              showActions={config?.showActions}
              data={paginatedData}
              editingRow={editingRow}
              editedValues={editedValues}
              handleCellChange={handleCellChange}
              selectedRows={selectedRows}
              handleRowSelect={handleRowSelect}
              handleEditRow={handleEditRow}
              handleSaveRow={handleSaveRow}
              handleCancelEdit={handleCancelEdit}
              handleDeleteRow={handleDeleteRow}
              columnWidths={columnWidths}
              showRowSelection={config.showRowSelection !== false}
            />
          </table>
        </div>
      )}

      {config.pagination && !isLoading && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems || filteredData?.length || 0}
          pageSize={config.pagination.pageSize || DEFAULT_PAGE_SIZE}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default TableUI;
